import type { IFilesystem } from '@livx.cc/wcli/core';

/**
 * Typed subagents — named child-agent definitions from `<dir>/<name>.md`, each with a
 * description, an optional model + tool allowlist (frontmatter), and a system prompt (the
 * body). The `Task` tool's `agentType` selects one, so a delegated sub-task runs with its
 * own persona, model, and scoped tools — the Claude-Code `.claude/agents` model.
 * VFS-backed (edge-portable), mirroring loadSkills / loadCommands.
 */
export interface AgentDef {
  name: string;
  description: string;
  /** The .md body — used as the child's system prompt (overrides the default). */
  systemPrompt?: string;
  /** Optional model override for this subagent type. */
  model?: string;
  /** Optional tool-name allowlist (scopes the child's tools). */
  tools?: string[];
}

/** Lean frontmatter parse for `description`/`model`/`tools` (no YAML dependency). */
function parseAgentFrontmatter(md: string): { description?: string; model?: string; tools?: string[]; body: string } {
  const m = md.match(/^---\s*\n([\s\S]*?)\n---\s*\n?/);
  const block = m ? m[1] : '';
  const get = (k: string) => {
    const r = new RegExp(`^${k}\\s*:\\s*(.+)$`, 'im').exec(block);
    return r ? r[1].trim().replace(/^["']|["']$/g, '') : undefined;
  };
  const toolsRaw = get('tools');
  const tools = toolsRaw
    ? toolsRaw.replace(/^\[|\]$/g, '').split(',').map((s) => s.trim().replace(/^["']|["']$/g, '')).filter(Boolean)
    : undefined;
  return { description: get('description'), model: get('model'), tools, body: (m ? md.slice(m[0].length) : md).trim() };
}

/** Discover subagent definitions under `dir` and a catalog block for the system prompt. */
export async function loadAgents(fs: IFilesystem, dir: string): Promise<{ agents: AgentDef[]; catalog: string }> {
  const agents: AgentDef[] = [];
  if (await fs.exists(dir)) {
    for (const entry of await fs.readDir(dir)) {
      if (!entry.endsWith('.md')) continue;
      const fm = parseAgentFrontmatter(await fs.readFile(`${dir}/${entry}`));
      agents.push({
        name: entry.replace(/\.md$/, ''),
        description: fm.description || '',
        systemPrompt: fm.body || undefined,
        model: fm.model,
        tools: fm.tools,
      });
    }
  }
  if (!agents.length) return { agents, catalog: '' };
  const catalog =
    '## Subagent types (pass as the `Task` tool `agentType`)\n' +
    agents.map((a) => `- **${a.name}** — ${a.description}`).join('\n');
  return { agents, catalog };
}
